import { useCallback, useSyncExternalStore } from 'react';

/**
 * Состояние генерации живёт вне React-дерева.
 *
 * Инвариант: студент может уйти со страницы во время генерации и вернуться — результат его дождётся.
 * Компонент размонтируется, а запрос и его результат остаются здесь, привязанные к ключу сценария.
 */

const IDLE = { status: 'idle', data: null, error: null, startedAt: null };

const state = new Map();
const listeners = new Map();
let epoch = 0;                 // растёт при выходе: ответы, запрошенные прошлым студентом, не долетают

function emit(key) {
  (listeners.get(key) || new Set()).forEach((fn) => fn());
}

function write(key, value) {
  state.set(key, value);
  emit(key);
}

/** Метка текущего студента: снимается перед запросом, проверяется перед записью результата. */
export function captureEpoch() {
  return epoch;
}

/** Запустить генерацию: результат осядет в хранилище, даже если страницу закрыли. */
export function runGeneration(key, factory) {
  const token = epoch;
  write(key, { status: 'running', data: null, error: null, startedAt: Date.now() });
  return factory()
    .then((data) => {
      if (token !== epoch) return null;            // за это время сменился пользователь
      write(key, { status: 'done', data, error: null, startedAt: null });
      return data;
    })
    .catch((err) => {
      if (token !== epoch) return null;
      const message = err?.response?.data?.error || 'Не получилось. Попробуйте ещё раз.';
      write(key, { status: 'error', data: null, error: message, startedAt: null });
      return null;
    });
}

/** Положить готовый результат без запроса — например, восстановленный из архива после перезагрузки. */
export function setGeneration(key, data, token = epoch) {
  if (token !== epoch) return;
  write(key, { status: 'done', data, error: null, startedAt: null });
}

export function clearGeneration(key) {
  write(key, IDLE);
}

/**
 * Забыть всё: вызывается при выходе и при потере сессии.
 *
 * Компьютеры в аудитории общие — без этого следующий студент увидел бы чужую задачу и чужой черновик.
 */
export function resetGenerations() {
  epoch += 1;
  const keys = [...state.keys()];
  state.clear();
  keys.forEach(emit);
}

/**
 * useState, переживающий уход со страницы: значение живёт в модуле, а не в дереве компонентов.
 * Для черновиков и раскрытых подсказок — всего, что жалко потерять при переходе между вкладками.
 */
export function useSessionState(key, initial) {
  const storeKey = `state:${key}`;
  if (!state.has(storeKey)) state.set(storeKey, initial);

  const subscribe = useCallback((cb) => {
    if (!listeners.has(storeKey)) listeners.set(storeKey, new Set());
    listeners.get(storeKey).add(cb);
    return () => listeners.get(storeKey).delete(cb);
  }, [storeKey]);
  const snapshot = useCallback(() => state.get(storeKey), [storeKey]);
  const value = useSyncExternalStore(subscribe, snapshot, snapshot);

  const setValue = useCallback((next) => {
    write(storeKey, typeof next === 'function' ? next(state.get(storeKey)) : next);
  }, [storeKey]);

  return [value, setValue];
}

export function useGeneration(key) {
  const subscribe = useCallback((cb) => {
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key).add(cb);
    return () => listeners.get(key).delete(cb);
  }, [key]);
  const snapshot = useCallback(() => state.get(key) || IDLE, [key]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
